import React, { useMemo } from 'react';
import Link from 'components/link';
import linkSizes from 'components/link/link-sizes';
import linkThemes from 'components/link/link-themes';
import LinkButton from 'components/link-button';
import linkButtonThemes from 'components/link-button/link-button-themes';
import MainMenuItem from './main-menu-item';
import { Accordion } from 'react-accessible-accordion';
import PropTypes from 'prop-types';
import cn from 'classnames';

const MainMenu = ({
  homeLink,
  items,
  downloadReportLink,
  className,
  navAriaLabel,
}) => {
  const mapMenuColumnItem = (item, index, itemsPerColumn) => {
    const itemIndex = itemsPerColumn ? index + itemsPerColumn : index;
    return {
      ...item,
      id: itemIndex,
    };
  };

  const splitItemsIntoColumns = items => {
    const totalItems = items.length;
    let itemsPerColumn;

    // Determine the number of items per column based on the total number of items
    if (totalItems > 12) {
      itemsPerColumn = Math.ceil(totalItems / 2);
    } else {
      itemsPerColumn = 6;
    }

    // Slice the items array into two columns
    const firstColumnItems = items
      .slice(0, itemsPerColumn)
      .map((item, index) => mapMenuColumnItem(item, index));
    const secondColumnItems = items
      .slice(itemsPerColumn)
      .map((item, index) => mapMenuColumnItem(item, index, itemsPerColumn));

    return [firstColumnItems, secondColumnItems];
  };

  const [firstColumnItems, secondColumnItems] = useMemo(
    () => splitItemsIntoColumns(items),
    [items]
  );

  const openedMenuItemsIndexes = useMemo(() => {
    return items.reduce((acc, item, index) => {
      if (item.items.some(subItem => subItem.isActive)) {
        acc.push(index);
      }
      return acc;
    }, []);
  }, [items]);

  return (
    <nav className={cn('main-menu', className)} aria-label={navAriaLabel}>
      {homeLink && (
        <div className="main-menu__home-link-holder">
          <Link
            className="main-menu__home-link link_main-menu-home-link"
            size={linkSizes.large}
            theme={linkThemes.light}
            {...homeLink}
          />
        </div>
      )}
      <Accordion
        allowMultipleExpanded={true}
        allowZeroExpanded={true}
        preExpanded={openedMenuItemsIndexes}
        className="main-menu__columns"
      >
        <div className="main-menu__column">
          {firstColumnItems.map(columnItem => (
            <MainMenuItem
              {...columnItem}
              key={columnItem.id}
              className="main-menu__item"
            />
          ))}
        </div>
        <div className="main-menu__column">
          {secondColumnItems.map(columnItem => (
            <MainMenuItem
              {...columnItem}
              key={columnItem.id}
              className="main-menu__item"
            />
          ))}
        </div>
      </Accordion>
      {downloadReportLink && (
        <LinkButton
          className="main-menu__button"
          iconName="download"
          download
          theme={linkButtonThemes.transparent}
          {...downloadReportLink}
        />
      )}
    </nav>
  );
};

MainMenu.viewModelMeta = {
  className: 'ignore',
  navAriaLabel: 'ignore',
};

MainMenu.propTypes = {
  className: PropTypes.string,
  navAriaLabel: PropTypes.string,
  homeLink: PropTypes.exact(Link.propTypes),
  items: PropTypes.arrayOf(PropTypes.exact(MainMenuItem.propTypes)).isRequired,
  downloadReportLink: PropTypes.exact(Link.propTypes),
};

export default MainMenu;
